import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { toast } from "react-toastify";
import {
  FaUserShield,
  FaUserPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaUsers,
  FaUserCheck,
  FaUserTimes,
  FaCrown,
} from "react-icons/fa";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import "./AdminManagement.css";

const PAGE_SIZE = 20;

export default function AdminList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    superadmins: 0,
  });

  const fetchAdmins = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: PAGE_SIZE,
        search: search || undefined,
        role: roleFilter || undefined,
        isActive: statusFilter === "active" ? true : statusFilter === "inactive" ? false : undefined,
      };

      const response = await allAPiServicesCall.listAdminUsers(params, getAuthConfig());

      if (response?.success) {
        setAdmins(response.data || []);
        setPagination(response.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });

        // Calculate stats from all admins (this is simplified - ideally should come from API)
        if (page === 1 && !search && !roleFilter && !statusFilter) {
          const allData = response.data || [];
          setStats({
            total: response.pagination?.total || allData.length,
            active: allData.filter(a => a.isActive !== false).length,
            inactive: allData.filter(a => a.isActive === false).length,
            superadmins: allData.filter(a => a.role === "superadmin").length,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
      toast.error("Failed to load admin users");
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter]);

  useEffect(() => {
    fetchAdmins(1);
  }, [fetchAdmins]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAdmins(1);
  };

  const getAdminId = (admin) => {
    const id = admin._id;
    if (typeof id === 'string') return id;
    if (id.$oid) return id.$oid;
    // Handle buffer format from MongoDB
    if (id.buffer) {
      const bytes = Object.values(id.buffer);
      return bytes.map(b => b.toString(16).padStart(2, '0')).join('');
    }
    return String(id);
  };

  const openDeleteModal = (admin) => {
    setAdminToDelete(admin);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setAdminToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!adminToDelete) return;

    try {
      setIsDeleting(true);
      const adminId = getAdminId(adminToDelete);

      // If admin is already inactive, permanently delete them
      if (adminToDelete.isActive === false) {
        await allAPiServicesCall.hardDeleteAdminUser(adminId, getAuthConfig());
      } else {
        // Otherwise, just deactivate them
        await allAPiServicesCall.deleteAdminUser(adminId, getAuthConfig());
      }

      fetchAdmins(pagination.page);
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting admin:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const goToPage = (page) => {
    fetchAdmins(page);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const parsed = new Date(date);
    if (isNaN(parsed.getTime())) return "-";
    return parsed.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <CommonLayout>
      <div className="admin-management-page container-fluid px-2 px-md-4">
        {/* Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
          <div>
            <h2 className="mb-1">
              <FaUserShield className="me-2" />
              Admin Management
            </h2>
            <p className="text-muted mb-0">Manage admin users and their permissions</p>
          </div>
          <Link to="/admin-management/add" className="btn btn-add">
            <FaUserPlus />
            Add New Admin
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-lg-3">
            <div className="stats-card">
              <div className="icon-wrapper primary">
                <FaUsers />
              </div>
              <div className="stats-content">
                <h3>{stats.total}</h3>
                <p>Total Admins</p>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="stats-card">
              <div className="icon-wrapper success">
                <FaUserCheck />
              </div>
              <div className="stats-content">
                <h3>{stats.active}</h3>
                <p>Active</p>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="stats-card">
              <div className="icon-wrapper danger">
                <FaUserTimes />
              </div>
              <div className="stats-content">
                <h3>{stats.inactive}</h3>
                <p>Inactive</p>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="stats-card">
              <div className="icon-wrapper warning">
                <FaCrown />
              </div>
              <div className="stats-content">
                <h3>{stats.superadmins}</h3>
                <p>Super Admins</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="action-header">
          <form onSubmit={handleSearch} className="filter-bar">
            <div className="position-relative">
              <FaSearch className="position-absolute" style={{ left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
              <input
                type="text"
                className="form-control"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingLeft: 38, minWidth: 250 }}
              />
            </div>
            <select
              className="form-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{ width: "auto", minWidth: 140 }}
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Super Admin</option>
            </select>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ width: "auto", minWidth: 140 }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <button type="submit" className="btn btn-primary">
              Search
            </button>
          </form>
        </div>

        {/* Admin List */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading admin users...</p>
          </div>
        ) : admins.length === 0 ? (
          <div className="empty-state">
            <div className="icon">
              <FaUsers />
            </div>
            <h3>No Admin Users Found</h3>
            <p>
              {search || roleFilter || statusFilter
                ? "Try adjusting your search filters"
                : "Get started by adding your first admin user"}
            </p>
            {!search && !roleFilter && !statusFilter && (
              <Link to="/admin-management/add" className="btn btn-add">
                <FaUserPlus className="me-2" />
                Add New Admin
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="admin-table d-none d-lg-block">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Permissions</th>
                    <th>Created</th>
                    <th>Last Login</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={getAdminId(admin)}>
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar">{getInitials(admin.fullName)}</div>
                          <div className="user-info">
                            <div className="name">{admin.fullName}</div>
                            <div className="email">{admin.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`role-badge ${admin.role}`}>
                          {admin.role === "superadmin" && <FaCrown className="me-1" />}
                          {admin.role}
                        </span>
                      </td>
                      <td>
                        <span className={`status-indicator ${admin.isActive !== false ? "active" : "inactive"}`}>
                          <span className="dot"></span>
                          {admin.isActive !== false ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        {admin.role === "superadmin" ? (
                          <span className="text-muted fst-italic">All permissions</span>
                        ) : (
                          <span>{admin.permissions?.length || 0} permissions</span>
                        )}
                      </td>
                      <td>{formatDate(admin.createdAt)}</td>
                      <td>{formatDate(admin.lastLogin)}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/admin-management/edit/${getAdminId(admin)}`)}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => openDeleteModal(admin)}
                            title="Deactivate"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="d-lg-none">
              <div className="row g-3">
                {admins.map((admin) => (
                  <div key={getAdminId(admin)} className="col-12 col-sm-6">
                    <div className="admin-card">
                      <div className="avatar-section">
                        <div className="avatar">{getInitials(admin.fullName)}</div>
                        <div className="admin-info">
                          <h4>{admin.fullName}</h4>
                          <p className="email">{admin.email}</p>
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <span className={`role-badge ${admin.role}`}>
                          {admin.role === "superadmin" && <FaCrown className="me-1" />}
                          {admin.role}
                        </span>
                        <span className={`status-indicator ${admin.isActive !== false ? "active" : "inactive"}`}>
                          <span className="dot"></span>
                          {admin.isActive !== false ? "Active" : "Inactive"}
                        </span>
                      </div>

                      {admin.role !== "superadmin" && admin.permissions?.length > 0 && (
                        <div className="permissions-preview">
                          <h5>Permissions</h5>
                          <div className="permission-tags">
                            {admin.permissions.slice(0, 3).map((perm) => (
                              <span key={perm} className="permission-tag">
                                {perm}
                              </span>
                            ))}
                            {admin.permissions.length > 3 && (
                              <span className="permission-tag more">
                                +{admin.permissions.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="card-actions">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => navigate(`/admin-management/edit/${getAdminId(admin)}`)}
                        >
                          <FaEdit className="me-1" /> Edit
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => openDeleteModal(admin)}
                        >
                          <FaTrash className="me-1" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-3">
                <div className="text-muted">
                  Showing {(pagination.page - 1) * pagination.limit + 1} -{" "}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={pagination.page <= 1}
                    onClick={() => goToPage(pagination.page - 1)}
                  >
                    Previous
                  </button>
                  <span className="px-3">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => goToPage(pagination.page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title={adminToDelete?.isActive === false ? "Permanently Delete Admin" : "Deactivate Admin"}
        message={
          adminToDelete
            ? adminToDelete.isActive === false
              ? `Are you sure you want to permanently delete "${adminToDelete.fullName}"? This action cannot be undone.`
              : `Are you sure you want to deactivate "${adminToDelete.fullName}"? They will no longer be able to access the admin panel.`
            : ''
        }
        confirmText={adminToDelete?.isActive === false ? "Delete Permanently" : "Deactivate"}
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </CommonLayout>
  );
}
